import axios from 'axios';
import { loadModules } from 'esri-loader';
import { layersConfig } from '../../services/portfolios/layersConfig';
import { allISO, allISOSubregions } from '../../constants/portfolios';

export const downloadScreenshot = (uri, name) => {
    var link = document.createElement('a');
    link.download = name;
    link.href = uri;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

export const getArcGISOAuthTokenDetails = async () => {
    const response = await axios.get('/arcgis-oath-token-details');
    return response.data;
};

export const getPowersimmLayerItemsInfo = async () => {
    const response = await axios.get(`/arcgis-schema-feature-layer`, {
        suppressErrorMessageHandler: true,
    });
    return response?.data;
};

export const getBookmarks = async () => {
    const response = await axios.get(`/map-bookmarks`);
    return response.data;
};

export const addBookmark = async (payload) => {
    const response = await axios.post(`/map-bookmarks`, payload);
    return response.data;
};

export const generateContinuousColorRenderer = async (
    mapView,
    layer,
    fieldName,
    colorName,
    theme,
    isVislble = false,
    storageKey = null
) => {
    const [colorSchemes, colorRendererCreator] = await loadModules([
        'esri/smartMapping/symbology/color',
        'esri/smartMapping/renderers/color',
    ]);

    const colorParams = {
        layer: layer,
        valueExpression: `$feature.${fieldName}`,
        view: mapView,
        colorScheme: colorSchemes.flipColors(
            colorSchemes.getSchemeByName({ name: colorName, geometryType: 'point', theme: theme })
        ),
        outlineOptimizationEnabled: true,
        defaultSymbolEnabled: false,
    };

    await colorRendererCreator.createContinuousRenderer(colorParams).then(async (response) => {
        const rendererResult = response;
        layer.renderer = rendererResult.renderer;
        layer.visible = isVislble;

        if (window?.localStorage && storageKey != null) {
            localStorage.setItem(storageKey, JSON.stringify(rendererResult.renderer.clone()));
        }
    });
};

export const clearIsoLocalStorage = (isoName) => {
    if (window?.localStorage) {
        const searchString = `ISORENDERER_${isoName}`;

        const matchingKeys = [];
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);

            if (key && key.includes(searchString)) {
                matchingKeys.push(key);
            }
        }

        matchingKeys?.forEach((key) => {
            localStorage.removeItem(key);
        });
    }
};

export const styleIsoLayerField = async (view, iso, isoLayer, isVislble, fieldName) => {
    const colorName = 'Blue and Red 9';
    const theme = 'above-and-below';
    const [ClassBreaksRenderer] = await loadModules(['esri/renderers/ClassBreaksRenderer']);

    if (window?.localStorage) {
        const storageKey = `ISORENDERER_${iso.name}_${fieldName}_${iso.version}`;
        const renderer = localStorage.getItem(storageKey);

        if (renderer) {
            isoLayer.renderer = ClassBreaksRenderer.fromJSON(JSON.parse(renderer));
            isoLayer.visible = isVislble;
        } else {
            // renderer not found - could be new version: remove all previous ISO renderers from storage
            clearIsoLocalStorage(iso.name);

            // generate and store ISO layer default field and version renderer
            await generateContinuousColorRenderer(view, isoLayer, fieldName, colorName, theme, isVislble, storageKey);
        }
    } else {
        await generateContinuousColorRenderer(view, isoLayer, fieldName, colorName, theme, isVislble);
    }
};

export const deleteMapElement = (id) => axios.delete(`/map-element/${id}`);

export const organizeMapLayers = (layers, view) => {
    let organizedLayers = [];
    let iso = [];
    let subregions = [];

    for (const layer of layers) {
        if (layer.title) {
            const layerInfo = layersConfig.find((elem) => elem.title === layer.title);
            layer.description = layerInfo ? layerInfo.description : '';
            layer.custom_title = layerInfo && layerInfo.custom_title ? layerInfo.custom_title : null;
        }

        layer.visibleAtScale = view.scale < layer.minScale || layer.minScale === 0;

        if (layer.title && allISO.includes(layer.title)) {
            const found = iso?.find((l) => l.title === layer.title);
            if (!found) iso.push(layer);
            continue;
        }

        if (layer.title && allISOSubregions.includes(layer.title)) {
            subregions.push(layer);
            continue;
        }

        //Custom PowerSimm Layer
        if (layer?.isPowerSimmLayer) {
            const found = organizedLayers?.find((l) => l.isPowerSimmLayer);
            if (!found) organizedLayers.push(layer);
            continue;
        }
    }

    // Sort groups
    iso = iso.sort((a, b) => (a.title > b.title ? 1 : b.title > a.title ? -1 : 0));
    subregions = subregions.sort((a, b) => (a.title > b.title ? 1 : b.title > a.title ? -1 : 0));

    const isoLayer = layers.find((elem) => elem.id === 'iso_node');
    isoLayer.childrenNodes = iso;
    organizedLayers.unshift(isoLayer);

    // Ascend ISO Forecasting Subregions
    const subregionsLayer = layers.find((elem) => elem.id === 'forecasting_subregions_node');
    subregionsLayer.childrenNodes = subregions;
    organizedLayers.unshift(subregionsLayer);

    return organizedLayers.sort(function (a, b) {
        return a.layerOrder - b.layerOrder;
    });
};
